import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Card from "@mui/material/Card";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import PaginationBar from "components/PaginationBar/PaginationBar";
import api from "utils/api";
import CreateInvoice from "../RaiseInvoice/CreateInvoice";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";

const currency = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ManualInvoices({ onBack }) {
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, limit: 10 });
  const [loading, setLoading] = useState(true);
  const [activeAction, setActiveAction] = useState("");
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(null);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/finance/manual-invoices", {
        params: { page, limit: pagination.limit, ...(query ? { search: query } : {}) },
      });
      setInvoices(response.data.data || []);
      setPagination((current) => ({ ...current, ...response.data.pagination }));
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load saved invoices.");
    } finally {
      setLoading(false);
    }
  }, [page, pagination.limit, query]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setQuery(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const openInvoice = async (invoice, download) => {
    setActiveAction(invoice._id);
    const tab = download ? null : window.open("about:blank", "_blank");
    if (!download && !tab) {
      setActiveAction("");
      toast.error("Allow popups to open the invoice.");
      return;
    }
    try {
      const response = await api.get(`/finance/manual-invoices/${invoice._id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      if (download) {
        const link = document.createElement("a");
        link.href = url;
        const parentFileName = invoice.parentName.replace(/[^a-z0-9 _-]/gi, "").trim().replace(/\s+/g, "_").slice(0, 80) || "Parent";
        link.download = `${parentFileName}-${invoice.invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        tab.location.href = url;
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to open saved invoice.");
    } finally {
      setActiveAction("");
    }
  };

  const startEdit = (invoice) => {
    setEditingInvoice(invoice);
  };
  const deleteInvoice = async (invoice) => {
    if (!invoice) return;
    setActiveAction(invoice._id);
    try { await api.delete(`/finance/manual-invoices/${invoice._id}`); toast.success("Invoice deleted."); setDeletingInvoice(null); await loadInvoices(); }
    catch (error) { toast.error(error.response?.data?.message || "Unable to delete invoice."); }
    finally { setActiveAction(""); }
  };

  const columns = [
    { name: "invoice", label: "Invoice #", align: "left" },
    { name: "parent", label: "Parent name", align: "left" },
    { name: "description", label: "Description", align: "left" },
    { name: "invoice date", label: "Invoice date", align: "left" },
    { name: "due date", label: "Due date", align: "left" },
    { name: "total", label: "Total", align: "right" },
    { name: "paid", label: "Paid", align: "right" },
    { name: "due", label: "Due amount", align: "right" },
    { name: "actions", label: "Actions", align: "center" },
  ];
  const rows = loading || invoices.length === 0 ? [{
    invoice: <ArgonTypography variant="caption" color="secondary">{loading ? "Loading invoices..." : "No saved invoices found."}</ArgonTypography>,
  }] : invoices.map((invoice) => ({
    invoice: <ArgonTypography variant="caption" fontWeight="medium" color="text">
      {invoice.invoiceNumber.startsWith("MAN-") && invoice.invoiceNumber.length > 12
        ? `MAN-${invoice.invoiceNumber.slice(-8)}`
        : invoice.invoiceNumber}
    </ArgonTypography>,
    parent: <ArgonTypography variant="caption" fontWeight="medium" color="text">{invoice.parentName}</ArgonTypography>,
    description: <ArgonTypography variant="caption" color="text">{invoice.subject}</ArgonTypography>,
    "invoice date": <ArgonTypography variant="caption" color="text">{dayjs(invoice.invoiceDate).format("DD MMM YYYY")}</ArgonTypography>,
    "due date": <ArgonTypography variant="caption" color="text">{dayjs(invoice.dueDate).format("DD MMM YYYY")}</ArgonTypography>,
    total: <ArgonTypography variant="caption" color="success" fontWeight="medium">{currency(invoice.total)}</ArgonTypography>,
    paid: <ArgonTypography variant="caption" color="text">{currency(invoice.paymentMade)}</ArgonTypography>,
    due: <ArgonTypography variant="caption" color="warning" fontWeight="medium">{currency(invoice.balanceDue)}</ArgonTypography>,
    actions: (
      <ArgonBox display="flex" justifyContent="center" alignItems="center">
        <Tooltip title="Open invoice"><span><IconButton color="primary" disabled={Boolean(activeAction)} onClick={() => openInvoice(invoice, false)}><VisibilityIcon /></IconButton></span></Tooltip>
        <Tooltip title="Download PDF"><span><IconButton color="primary" disabled={Boolean(activeAction)} onClick={() => openInvoice(invoice, true)}>{activeAction === invoice._id ? <CircularProgress size={20} /> : <DownloadIcon />}</IconButton></span></Tooltip>
        <Tooltip title="Edit invoice"><span><IconButton color="primary" disabled={Boolean(activeAction)} onClick={() => startEdit(invoice)}><EditIcon /></IconButton></span></Tooltip>
        <Tooltip title="Delete invoice"><span><IconButton color="error" disabled={Boolean(activeAction)} onClick={() => setDeletingInvoice(invoice)}><DeleteIcon /></IconButton></span></Tooltip>
      </ArgonBox>
    ),
  }));
  return (
    <>
      <Card>
          <ArgonBox p={{ xs: 2, sm: 3 }}>
            <ArgonBox display="flex" justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} flexWrap="wrap" gap={2} mb={3}>
              <ArgonBox>
                <ArgonButton variant="text" color="secondary" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1, px: 0 }}>
                  Back to Raise Invoice
                </ArgonButton>
                <ArgonTypography variant="h5" fontWeight="medium">Manual Invoices</ArgonTypography>
                <ArgonTypography variant="button" color="text">Saved invoices created by admins</ArgonTypography>
              </ArgonBox>
              <ArgonBox display="flex" alignItems="center" gap={1} sx={{ width: { xs: "100%", sm: 280 } }}>
                <ArgonInput size="small" fullWidth value={search} placeholder="Search by parent name"
                  onChange={(event) => setSearch(event.target.value)} inputProps={{ maxLength: 200 }} />
              </ArgonBox>
            </ArgonBox>
            <ArgonBox sx={{ width: "100%", overflowX: "auto" }}>
              <Table columns={columns} rows={rows} />
            </ArgonBox>
            {!loading && pagination.total > 0 && <PaginationBar currentPage={page} rowsPerPage={pagination.limit} totalItems={pagination.total} totalPages={pagination.pages} onPageChange={(_, nextPage) => setPage(nextPage)} onRowsPerPageChange={(event) => { setPagination((current) => ({ ...current, limit: Number(event.target.value) })); setPage(1); }} />}
          </ArgonBox>
      </Card>
      {editingInvoice && <CreateInvoice initialInvoice={editingInvoice} onClose={() => setEditingInvoice(null)} onSaved={loadInvoices} />}
      <DeleteDialog open={Boolean(deletingInvoice)} onClose={() => !activeAction && setDeletingInvoice(null)}
        onConfirm={() => deleteInvoice(deletingInvoice)} title="Are you sure you want to delete this manual invoice?"
        description={`Delete invoice ${deletingInvoice?.invoiceNumber}?`} confirmText={activeAction ? "Deleting..." : "Delete"} cancelText="Cancel" />
    </>
  );
}

ManualInvoices.propTypes = {
  onBack: PropTypes.func.isRequired,
};
